<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<!--
    MomentsWithConnections.vue
    This is the new "With Connections" version of the Moments component.
    Feature-flagged via: performance_management_connections_integration
-->
<template>
    <div ref="pageContent" class="flex flex-col gap-4" :style="showReviewInput ? { paddingBottom: `${dockHeight + 32}px` } : undefined">
        <!-- Timeline navigation, one tab per moment -->
        <BaseTabs
            v-if="moments.length > 0"
            v-model="activeTabId"
            :tab-ids="tabIds"
            :labels="tabLabels"
            :disabled-tab-ids="lockedTabIds"
            list-class="max-w-4xl mx-auto px-4"
            content-class="pt-4">
            <template #tab="{ tabId, selected }">
                <MomentsTimeline
                    :moment-title="momentsByTabId[tabId].title"
                    :date="formatDate(momentsByTabId[tabId].start_date)"
                    :state="momentsByTabId[tabId].state"
                    :is-active="selected"
                    :is-preview="momentsByTabId[tabId].visibility === 'preview'" />
            </template>
            <template v-for="moment in moments" :key="moment.id" #[`content-${moment.id}`]>
                <PerformanceMomentEndOfYear :moment="moment" :has-people-to-review="hasPeopleToReview" :can-edit-team="canEditTeam" :review-entry-enabled="reviewEntryEnabled" />
            </template>
        </BaseTabs>

        <NoMomentsNotification v-else />

        <!-- On desktop, opaque from the input down to the bottom edge, like the chat composer, so the page scrolls behind it;
             it also takes the clicks there so hidden cards cannot be activated through it. -->
        <div
            v-if="showReviewInput"
            ref="floatingInput"
            class="review-chat-dock pointer-events-none fixed z-20 md:pointer-events-auto md:bg-surface"
            :style="{ left: `${contentLeft}px`, width: `${contentWidth}px` }">
            <HomeChatInput
                class="pointer-events-auto"
                compact
                validate-pii-on-send
                chat-source="performance_management_page"
                location="performance_management_page"
                textarea-max-length="5000"
                :pii-patterns="piiPatterns"
                :auto-focus-input="false"
                disable-attachments
                idempotent-start
                :document-upload-enabled="false"
                default-chat-redirect-url="/performance_management"
                start-chat-url="/v1/chat/start-performance-management"
                :placeholder="t('chat.performancePicker.entryPlaceholder', { moment: momentsByTabId[activeTabId]?.title ?? '' })" />
        </div>

        <template v-if="organizationCards.length > 0">
            <!-- Same column and section spacing as the moment content above. -->
            <div class="max-w-4xl mx-auto w-full px-0 md:px-4 mt-6">
                <h3 class="scaffold-body mb-4">{{ t("moments.page.organizationTopicsHeading", { organizationName }) }}</h3>
                <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <NewHomeCard
                        v-for="card in organizationCards"
                        :key="card.id"
                        :card-type="card.type"
                        :title="card.title"
                        :subtitle="card.subtitle ?? undefined"
                        :content="card.content"
                        :cta-button-text="card.ctaButtonText"
                        @cta-click="handleOrganizationCardClick(card)">
                        <template #header>
                            <img v-if="card.image_url" :src="card.image_url" :alt="card.title || t('moments.page.cardImageAlt')" class="h-10 w-auto object-contain max-w-50" />
                            <img
                                v-else-if="organizationDetails.logo_url"
                                :src="organizationDetails.logo_url"
                                :alt="organizationDetails.name || t('moments.page.organizationLogoAlt')"
                                class="h-10 w-auto object-contain max-w-50" />
                            <i v-else class="bi bi-people text-3xl/none" aria-hidden="true"></i>
                        </template>
                    </NewHomeCard>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { useElementBounding, useElementSize } from "@vueuse/core";
import HomeChatInput from "~vue/ChatHome/HomeChatInput.vue";
import NewHomeCard from "~vue/ChatHome/NewHomeCard.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import MomentsTimeline from "~vue/components/moments/MomentsTimeline.vue";
import { useKeyboard } from "~vue/composables/useKeyboard";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import NoMomentsNotification from "~vue/Moments/NoMomentsNotification.vue";
import PerformanceMomentEndOfYear from "~vue/Moments/PerformanceMomentEndOfYear.vue";
import type { OrganizationCard } from "~vue/types/chat_home";
import type { PerformanceMoment as PerformanceMomentType } from "~vue/types/performance_management";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE, HOMEPAGE_CARD_LOCATION } from "~vue/utils/constants";
import { formatDate as localizedDate } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { PatternEntry } from "~vue/utils/pii/keywordClassifier";
import { computed, inject, type Ref, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    moments: PerformanceMomentType[];
    organizationCards?: OrganizationCard[];
    hasPeopleToReview: boolean | null;
    canEditTeam?: boolean;
    reviewEntryEnabled?: boolean;
    piiPatterns?: PatternEntry[] | null;
}

const props = withDefaults(defineProps<Props>(), {
    organizationCards: () => [],
    canEditTeam: true,
    reviewEntryEnabled: false,
    piiPatterns: null,
});

const { t } = useI18n();
const hasStartedMoment = computed(() => {
    const today = new Date().toISOString().slice(0, 10);
    return props.moments.some((moment) => moment.state !== "locked" && moment.start_date <= today && moment.ctas.length > 0);
});

const showReviewInput = computed(() => props.reviewEntryEnabled && hasStartedMoment.value);
const pageContent = useTemplateRef<HTMLElement>("pageContent");
const floatingInput = useTemplateRef<HTMLElement>("floatingInput");
const { left: contentLeft, width: contentWidth } = useElementBounding(pageContent);
const { height: dockHeight } = useElementSize(floatingInput, undefined, { box: "border-box" });
const { keyboardHeight } = useKeyboard();
const mobileInputBottom = computed(() =>
    keyboardHeight.value ? "calc(100vh - var(--visible-viewport-height, 100vh) + 1rem)" : "calc(var(--mobile-nav-total-height, 4rem) + 1rem)",
);

const getInitialMomentSelection = () => {
    // Deep link from the homepage banner's per-moment buttons (ENG-1330)
    const momentIdParam = new URLSearchParams(globalThis.location.search).get("moment_id");
    if (momentIdParam) {
        const requestedMoment = props.moments.find((m) => m.id === Number(momentIdParam) && m.state !== "locked");
        if (requestedMoment) {
            return requestedMoment;
        }
    }
    const latestMoments = [...props.moments].reverse();
    return (
        latestMoments.find((m) => m.state === "open") || latestMoments.find((m) => m.state === "completed") || latestMoments.find((m) => m.state === "expired") || props.moments[0]
    );
};
const defaultActiveMoment = getInitialMomentSelection();
const activeMomentId = ref<number | null>(defaultActiveMoment?.id || null);

watch(activeMomentId, (moment_id) => {
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}moment_clicked`, { moment_id });
});

// BaseTabs keys tabs by string; moments are keyed by number.
const tabIds = computed(() => props.moments.map((moment) => String(moment.id)));
const momentsByTabId = computed(() => Object.fromEntries(props.moments.map((moment) => [String(moment.id), moment])));
const tabLabels = computed(() => Object.fromEntries(props.moments.map((moment) => [String(moment.id), moment.title])));
// A locked moment has not opened yet, so it cannot be selected.
const lockedTabIds = computed(() => props.moments.filter((moment) => moment.state === "locked").map((moment) => String(moment.id)));
const activeTabId = computed({
    get: () => (activeMomentId.value === null ? "" : String(activeMomentId.value)),
    set: (tabId: string) => {
        activeMomentId.value = Number(tabId);
    },
});

const formatDate = (date: string): string => {
    if (!date) {
        return "";
    }
    return localizedDate(date, "month-year");
};

interface OrganizationDetails {
    is_default?: boolean;
    name?: string;
    logo_url?: string;
}

const organizationDetails = inject<Ref<OrganizationDetails>>("organization_details", ref({}));
const organizationName = computed(() => {
    if (organizationDetails.value?.is_default || !organizationDetails.value?.name) {
        return t("moments.page.defaultOrganizationName");
    }
    return organizationDetails.value.name;
});
const handleOrganizationCardClick = (card: OrganizationCard) => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, { card_type: card.type, location: HOMEPAGE_CARD_LOCATION.MOMENTS });
    openUrl(card.url, { queryParams: { chat_source: CHATSOURCE.TRENDING_ORG_PROMPT } });
};

// The removed "Looking for your old conversations?" toggle persisted reverted moment IDs
// under this key; clear it so stale data doesn't linger in browsers.
globalThis.localStorage?.removeItem("revert_to_old_ui_moment_ids");
</script>

<style scoped>
.review-chat-dock {
    bottom: v-bind(mobileInputBottom);
}

@media (min-width: 768px) {
    .review-chat-dock {
        bottom: 0;
        padding-bottom: 1.5rem;
    }
}
</style>
